<!--
  DJvirtualCommerce.vue - 迪加虚商管理页面（主组件）

  【页面说明】
  这是迪加虚商的管理后台主页面，采用 Tab 页签的形式组织各个功能模块。
  本组件作为"壳"组件，负责：
  1. 管理 Tab 页签的切换
  2. 初始化共享数据（代理商列表、企业列表等）
  3. 根据用户角色控制 Tab 的显示/隐藏
  4. 将共享数据通过 props 传递给各个子组件

  【Tab 页签列表】
  - 查询：查询单个号码的账户信息、话费余量、充值历史等
  - 批量查询：通过文件上传或手动输入批量查询号码
  - 开户审核查询：查询号码的开户审核状态
  - 停复机：管理号码的停机和复机操作
  - 异常话单：查看异常通话记录
  - 充值：单笔充值、异常充值处理、APP充值、返销
  - 批量充值：通过文件批量充值
  - 限制返档：管理号码的返档限制
  - 取消停机保号：管理停机保号状态
  - 接通限频：管理号码的接通频率限制
  - 充值套餐：管理充值套餐（使用独立组件）
  - 套餐管理：管理套餐配置（使用独立组件）
  - 漫游管控：管理号码的漫游状态
  - 自选套餐冻结：管理套餐冻结/解冻
  - 返销记录：查看返销记录
  - 资金池：查看和管理资金池余额
  - 充值日志：查看充值操作日志（使用独立组件）
  - 账务统计：查看账务统计报表（使用独立组件）

  【权限说明】
  - admin（管理员）：可以看到所有 Tab
  - agent（代理商）：只能看到查询、批量查询、开户审核、异常话单、限制返档、漫游管控、自选套餐冻结
-->
<template>
  <div class="container">
    <!--
      el-tabs：Element UI 的 Tab 页签组件
      v-model="activeName"：双向绑定当前激活的 Tab 名称
      @tab-click：点击 Tab 时触发的事件
      :class：根据当前 Tab 名称动态添加 CSS 类（用于不同 Tab 的样式调整）
    -->
    <el-tabs v-model="activeName" @tab-click="handleClick" :class="'custom-tabs-' + activeName">

      <!--
        【查询 Tab】
        功能：查询单个号码的详细信息
        v-if="isAdmin||isAgent"：只有管理员和代理商能看到这个 Tab
        :theme="theme"：传递主题颜色给子组件
        :theme3="theme3"：传递辅助主题颜色
        :is-admin="isAdmin"：传递是否为管理员标识
        :token="token"：传递请求头（包含认证信息）
      -->
      <el-tab-pane label="查询" v-if="isAdmin||isAgent" name="userInfo">
        <DjQueryTab :theme="theme" :theme3="theme3" :is-admin="isAdmin" :token="token" />
      </el-tab-pane>

      <!--
        【批量查询 Tab】
        功能：通过文件上传或手动输入批量查询号码信息
        :path="path"：传递项目的 base URL（用于文件上传）
      -->
      <el-tab-pane label="批量查询" v-if="isAdmin||isAgent" name="userInfoBatch">
        <DjBatchQueryTab :theme="theme" :path="path" :token="token" />
      </el-tab-pane>

      <!-- 【开户审核查询 Tab】查询号码的开户审核状态 -->
      <el-tab-pane label="开户审核查询" v-if="isAdmin||isAgent" name="accountReview">
        <DjAccountReviewTab :theme="theme" />
      </el-tab-pane>

      <!--
        【停复机 Tab】
        功能：管理号码的停机和复机操作
        v-if="isAdmin"：只有管理员能看到
        :agents="agents"：传递代理商列表（用于下拉选择筛选）
      -->
      <el-tab-pane label="停复机" v-if="isAdmin" name="restartRecordList">
        <DjRestartTab :theme="theme" :theme3="theme3" :agents="agents" :token="token" />
      </el-tab-pane>

      <!-- 【异常话单 Tab】查看异常通话记录 -->
      <el-tab-pane label="异常话单" v-if="isAdmin||isAgent" name="DJAbnormalCallList">
        <DjAbnormalTab :theme="theme" :is-admin="isAdmin" :agents="agents" />
      </el-tab-pane>

      <!--
        【充值 Tab】
        功能：单笔充值、异常充值处理、APP充值、返销操作
      -->
      <el-tab-pane label="充值" v-if="isAdmin" name="djRecharge">
        <DjRechargeTab :theme="theme" :theme3="theme3" :agents="agents" />
      </el-tab-pane>

      <!-- 【批量充值 Tab】通过文件批量充值号码 -->
      <el-tab-pane label="批量充值" v-if="isAdmin" name="djBatchRecharge">
        <DjBatchRechargeTab :theme="theme" :path="path" :token="token" />
      </el-tab-pane>

      <!-- 【限制返档 Tab】管理号码的返档限制 -->
      <el-tab-pane label="限制返档" v-if="isAdmin||isAgent" name="activationList">
        <DjActivationTab :theme="theme" :theme3="theme3" :token="token" />
      </el-tab-pane>

      <!-- 【取消停机保号 Tab】管理号码的停机保号状态 -->
      <el-tab-pane label="取消停机保号" v-if="isAdmin" name="NoShutdownMaintenanceNumber">
        <DjNoShutdownTab :theme="theme" :token="token" />
      </el-tab-pane>

      <!-- 【接通限频 Tab】管理号码的接通频率限制 -->
      <el-tab-pane label="接通限频" v-if="isAdmin" name="frequencyLimit">
        <DjFrequencyLimitTab :theme="theme" />
      </el-tab-pane>

      <!--
        【充值套餐 Tab】
        使用独立组件 DxtPackageTopUp，不需要拆分
        功能：管理充值套餐配置
      -->
      <el-tab-pane label="充值套餐" v-if="isAdmin" name="dxtPackageTopUp">
        <DxtPackageTopUp />
      </el-tab-pane>

      <!-- 【套餐管理 Tab】使用独立组件，管理套餐配置 -->
      <el-tab-pane label="套餐管理" v-if="isAdmin" name="dxtPackages">
        <DxtPackagesManagement />
      </el-tab-pane>

      <!-- 【漫游管控 Tab】管理号码的漫游状态 -->
      <el-tab-pane label="漫游管控" v-if="isAdmin||isAgent" name="numberControl">
        <DjNumberControlTab :theme="theme" />
      </el-tab-pane>

      <!-- 【自选套餐冻结 Tab】管理套餐的冻结和解冻 -->
      <el-tab-pane label="自选套餐冻结" v-if="isAdmin||isAgent" name="DJfreeze">
        <DjFreezeTab :theme="theme" />
      </el-tab-pane>

      <!-- 【返销记录 Tab】查看返销操作的历史记录 -->
      <el-tab-pane label="返销记录" v-if="isAdmin" name="djRebateList">
        <DjRebateTab :theme="theme" />
      </el-tab-pane>

      <!-- 【资金池 Tab】查看和管理资金池余额 -->
      <el-tab-pane label="资金池" v-if="isAdmin" name="djCapitalPool">
        <DjCapitalPoolTab :theme="theme" :theme3="theme3" />
      </el-tab-pane>

      <!-- 【充值日志 Tab】使用独立组件，查看充值操作日志 -->
      <el-tab-pane label="充值日志" v-if="isAdmin" name="dxtPackageTopUpLog">
        <DxtPackageTopUpLog />
      </el-tab-pane>

      <!-- 【账务统计 Tab】使用独立组件，查看账务统计报表 -->
      <el-tab-pane label="账务统计" v-if="isAdmin" name="dxtAccountStatistics">
        <DxtAccountStatistics />
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
/**
 * 导入 API 接口
 * fetchAgent：获取代理商列表的接口
 * fetchDefaultCompanies：获取企业列表的接口
 */
import {
  fetchAgent
} from '../../api/agentIndex';
import {
  fetchDefaultCompanies
} from '../../api/companyIndex';

/**
 * 导入已有的独立组件（这些组件之前就已经是独立文件，不需要拆分）
 * DxtPackagesManagement：套餐管理组件
 * DxtPackageTopUp：充值套餐组件
 * DxtPackageTopUpLog：充值日志组件
 * DxtAccountStatistics：账务统计组件
 */
import DxtPackagesManagement from '../system/DxtPackagesManagement.vue';
import DxtPackageTopUp from '../system/DxtPackageTopUp.vue';
import DxtPackageTopUpLog from '../system/DxtPackageTopUpLog.vue';
import DxtAccountStatistics from '../system/DxtAccountStatistics.vue';

/**
 * 导入本次拆分的 Tab 子组件
 * 这些组件分别对应不同的功能 Tab
 */
import DjQueryTab from './tabs/DjQueryTab.vue';           // 查询
import DjBatchQueryTab from './tabs/DjBatchQueryTab.vue'; // 批量查询
import DjAccountReviewTab from './tabs/DjAccountReviewTab.vue'; // 开户审核查询
import DjRestartTab from './tabs/DjRestartTab.vue';       // 停复机
import DjAbnormalTab from './tabs/DjAbnormalTab.vue';     // 异常话单
import DjRechargeTab from './tabs/DjRechargeTab.vue';     // 充值
import DjBatchRechargeTab from './tabs/DjBatchRechargeTab.vue'; // 批量充值
import DjActivationTab from './tabs/DjActivationTab.vue'; // 限制返档
import DjNoShutdownTab from './tabs/DjNoShutdownTab.vue'; // 取消停机保号
import DjFrequencyLimitTab from './tabs/DjFrequencyLimitTab.vue'; // 接通限频
import DjNumberControlTab from './tabs/DjNumberControlTab.vue';   // 漫游管控
import DjFreezeTab from './tabs/DjFreezeTab.vue';         // 自选套餐冻结
import DjRebateTab from './tabs/DjRebateTab.vue';         // 返销记录
import DjCapitalPoolTab from './tabs/DjCapitalPoolTab.vue'; // 资金池

export default {
  // 组件注册：将所有子组件注册到当前组件
  components: {
    DxtPackagesManagement,
    DxtPackageTopUp,
    DxtPackageTopUpLog,
    DxtAccountStatistics,
    DjQueryTab,
    DjBatchQueryTab,
    DjAccountReviewTab,
    DjRestartTab,
    DjAbnormalTab,
    DjRechargeTab,
    DjBatchRechargeTab,
    DjActivationTab,
    DjNoShutdownTab,
    DjFrequencyLimitTab,
    DjNumberControlTab,
    DjFreezeTab,
    DjRebateTab,
    DjCapitalPoolTab,
  },
  name: 'DJvirtualCommerce', // 组件名称

  data() {
    return {
      // ========== 主题样式相关 ==========
      // theme：主主题颜色（用于按钮、选中状态等）
      theme: localStorage.getItem('theme'),
      // theme3：辅助主题颜色（用于某些特定元素）
      theme3: localStorage.getItem('theme3'),
      // theme8：强调色主题（用于 Tab 激活状态、分页器等）
      theme8: localStorage.getItem('theme8'),

      // ========== 项目配置 ==========
      // path：项目的 base URL，用于文件上传等场景
      // 例如：开发环境是 "/"，生产环境可能是 "/dist/"
      path: process.env.BASE_URL,

      // token：请求头信息，每个 API 请求都需要携带
      // - Authorization：用户登录后获取的认证令牌
      // - type：请求类型标识
      // - UserRole：用户角色
      // - XSecurityVersion：安全版本号
      token: {
        Authorization: localStorage.getItem('token'),
        type: 'web',
        UserRole: localStorage.getItem('ms_role'),
        XSecurityVersion: 'v2',
      },

      // ========== 用户角色标识 ==========
      // 根据 localStorage 中存储的角色信息判断当前用户类型
      // 不同角色能看到不同的 Tab 页签
      isAdmin: false,     // 管理员：可以看到所有功能
      isAgent: false,     // 代理商：只能看到部分功能
      isCompany: false,   // 企业用户：目前未使用
      isNormal: false,    // 普通用户：目前未使用
      isSubAgent: false,  // 子代理：目前未使用

      // ========== Tab 状态 ==========
      // activeName：当前激活的 Tab 名称，默认显示"查询"
      activeName: 'userInfo',

      // ========== 共享数据 ==========
      // agents：代理商列表，用于下拉选择筛选
      // 获取方式：调用 fetchAgent API
      agents: [],

      // companys：企业列表，用于下拉选择筛选
      // 获取方式：调用 fetchDefaultCompanies API
      companys: [],

      // departments：部门列表，目前未使用
      departments: [],
    };
  },

  /**
   * created 钩子：组件实例被创建后立即执行
   * 主要任务：
   * 1. 判断当前用户角色
   * 2. 初始化共享数据（代理商列表）
   * 3. 设置主题样式
   */
  created() {
    // 从 localStorage 获取用户角色
    let role = localStorage.getItem('ms_role');

    // 初始化数据：获取代理商列表和企业列表
    this.init();

    // 根据角色设置对应的标识
    if (role === 'admin') {
      this.isAdmin = true;     // 管理员
    }
    if (role === 'agent') {
      this.isAgent = true;     // 代理商
    }
    if (role === 'normal') {
      this.isNormal = true;    // 普通用户
    }
    if (role === 'company') {
      this.isCompany = true;   // 企业用户
    }

    // 动态设置 Tab 和分页器的主题样式
    this.updateTabStyle();
    this.updatePaginationStyle();
  },

  methods: {
    /**
     * handleClick - Tab 点击事件处理
     * 当用户点击不同的 Tab 时，更新 activeName
     * @param {Object} tab - 被点击的 Tab 信息
     */
    handleClick(tab) {
      this.activeName = tab.props.name;
    },

    /**
     * init - 初始化数据
     * 1. 获取当前用户的代理商列表
     * 2. 获取企业列表
     */
    init() {
      // 调用 API 获取代理商列表
      // localStorage.getItem('user') 获取当前登录用户的 ID
      fetchAgent(localStorage.getItem('user')).then(res => {
        this.agents = res.data; // 将代理商列表保存到 agents
      });

      // 获取企业列表
      this.getfetchDefaultCompanies();
    },

    /**
     * getfetchDefaultCompanies - 获取企业列表
     * 用于在各个子组件中提供企业下拉选择
     * @param {Object} data - 请求参数
     *   - currentUserId：当前用户 ID
     *   - subAgentId：子代理 ID（可选）
     */
    getfetchDefaultCompanies() {
      let data = {
        currentUserId: localStorage.getItem('user'),
        subAgentId: ''
      };
      fetchDefaultCompanies(data).then(res => {
        if (res?.code === 200) {
          this.companys = res.data; // 将企业列表保存到 companys
        } else {
          this.$message.error(res.message); // 显示错误提示
        }
      });
    },

    /**
     * updatePaginationStyle - 动态设置分页器的主题颜色
     * 通过创建 <style> 标签注入 CSS，覆盖 Element UI 的默认样式
     * 这样可以让分页器的颜色与用户选择的主题保持一致
     */
    updatePaginationStyle() {
      // 查找或创建 style 标签
      let pagination = document.getElementById('pagination');
      if (!pagination) {
        pagination = document.createElement('style');
        pagination.id = 'dynamic-tab-style';
        document.head.appendChild(pagination);
      }
      // 注入分页器的自定义样式
      pagination.innerHTML = `
        .el-pager .active {
          background-color: ${this.theme8} !important;  /* 当前页码背景色 */
          color: #fff !important;                        /* 当前页码文字颜色 */
        }
        .el-pager .number:hover {
          color: ${this.theme8} !important;              /* 页码 hover 颜色 */
        }
        .el-pager .active:hover {
          color: #fff !important;                        /* 当前页码 hover 保持白色 */
        }
        .el-pager .more:hover {
          color: ${this.theme8} !important;              /* 省略号 hover 颜色 */
        }
      `;
    },

    /**
     * updateTabStyle - 动态设置 Tab 页签的主题颜色
     * 通过创建 <style> 标签注入 CSS，覆盖 Element UI 的默认样式
     * 这样可以让 Tab 的颜色与用户选择的主题保持一致
     */
    updateTabStyle() {
      // 查找或创建 style 标签
      let styleEl = document.getElementById('dynamic-tab-style');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'dynamic-tab-style';
        document.head.appendChild(styleEl);
      }
      // 注入 Tab 的自定义样式
      styleEl.innerHTML = `
        .el-tabs__active-bar {
          background-color: ${this.theme8} !important;  /* Tab 底部激活条颜色 */
        }
        .el-tabs__item.is-active {
          color: ${this.theme8} !important;              /* 激活 Tab 文字颜色 */
        }
        .el-tabs__item:hover {
          color: ${this.theme8} !important;              /* Tab hover 颜色 */
          opacity: 0.8;
        }
      `;
    },
  }
};
</script>

<style scoped>
/*
  【样式说明】
  以下样式用于页面的整体布局和通用组件样式
  scoped 表示这些样式只对当前组件生效，不会影响其他组件

  注意：这些样式是页面级别的通用样式
  各个 Tab 子组件的样式在各自的文件中定义
*/

/* handle-box：操作栏容器，包含搜索框、按钮等 */
.handle-box {
  margin-bottom: 20px;
}

/* handle-input：搜索输入框 */
.handle-input {
  width: 200px;
  display: inline-block;
}

/* recharge-box：充值选项容器 */
/* 隐藏 radio 按钮的原生样式 */
.recharge-box .el-radio__input {
  display: none;
}

/* 充值选项的边框样式 */
.recharge-box .el-radio.is-bordered {
  padding: 8px 14px 0 5px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  height: 30px;
}

/* table-box：表格容器，采用 flex 布局 */
.table-box {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  margin-bottom: 40px;
}

/* user-info-box：用户信息展示区域 */
.user-info-box {
  width: 600px;
}

/* user-info-tit：用户信息标题样式 */
.user-info-tit {
  background: #fafafa;
  color: #000;
  font-weight: 600;
  text-align: center;
  line-height: 34px;
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
}

/* info-box：信息行容器 */
.info-box {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  line-height: 34px;
  border-bottom: 1px solid #ebeef5;
  border-right: 1px solid #ebeef5;
}

/* info-name：信息标签（左侧标题） */
.info-name {
  background: #fafafa;
  border-right: 1px solid #ebeef5;
  border-left: 1px solid #ebeef5;
  width: 36%;
  padding-left: 10px;
  font-weight: 600;
}

/* info：信息值（右侧内容） */
.user-info-box .info-box .info {
  padding-left: 10px;
  color: #226003; /* 绿色表示有效数据 */
  width: 80%;
}

/* down：下载按钮样式 */
.down {
  border: 1px solid #dcdfe6;
  background: #4f7afd;
  color: #ffffff;
  transition: 0.1s;
  font-weight: 500;
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 4px;
  display: inline-block;
}

/* region_list：区域列表容器 */
.region_list {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  width: 30%;
}

/* region_list_tit：区域标题 */
.region_list_tit {
  font-size: 16px;
  font-weight: 600;
}

/* region_list_info：区域信息 */
.region_list_info {
  font-size: 16px;
}

/* region_box：区域容器 */
.region_box {
  width: 75%;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-box-pack: justify;
  -webkit-justify-content: space-around;
  -ms-flex-pack: distribute;
  justify-content: space-around;
  margin-bottom: 20px;
}

/* grid-content：统计卡片内容区域 */
.grid-content {
  display: flex;
  align-items: center;
  height: 100px;
}

/* /deep/ 深度选择器：穿透到子组件修改 Element UI 的样式 */
/deep/ .el-col-16 {
  flex: 0 0 59.9%;
}

/* grid-cont-right：统计数字右侧区域 */
.grid-cont-right {
  flex: 1;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  color: #999;
}

/* grid-num：统计数字 */
.grid-num {
  font-size: 30px;
  font-weight: bold;
}

/* grid-con-icon：统计图标 */
.grid-con-icon {
  font-size: 50px;
  width: 100px;
  height: 100px;
  text-align: center;
  line-height: 100px;
  color: #fff;
}

/* 不同统计卡片的图标渐变背景 */
.grid-con-1 .grid-con-icon {
  background: linear-gradient(#ffb199, #ff0844); /* 红色渐变 */
}

/* new：新建标识 */
.new {
  color: #b5b4b4;
  font-size: 12px;
}

/* 不同统计卡片的数字颜色 */
.grid-con-1 .grid-num {
  color: #4f7afd;
}

.grid-con-2 .grid-con-icon {
  background: linear-gradient(#48c6ef, #6f86d6); /* 蓝色渐变 */
}

.grid-con-2 .grid-num {
  color: #4f7afd;
}

.grid-con-3 .grid-con-icon {
  background: linear-gradient(#e5b2ca, #7028e4); /* 紫色渐变 */
}

.grid-con-3 .grid-num {
  color: #4f7afd;
}

.grid-con-4 .grid-con-icon {
  background: linear-gradient(#00c6fb, #005bea); /* 深蓝渐变 */
}

.grid-con-4 .grid-num {
  color: #4f7afd;
}

.grid-con-5 .grid-con-icon {
  background: linear-gradient(#89f7fe, #66a6ff); /* 浅蓝渐变 */
}

.grid-con-5 .grid-num {
  color: #4f7afd;
}

/* status_color2：状态标签样式（红色） */
.status_color2 {
  color: #fff;
  background-color: #ff0000;
  border-radius: 3px;
  display: inline-block;
  width: 60px;
  height: 26px;
  padding: 0 6px;
  text-align: center;
  line-height: 26px;
  font-size: 12px;
}

/* bgred：红色背景 */
.bgred {
  background-color: #ff0000;
}

/* bggreen：绿色背景 */
.bggreen {
  background-color: #67c23a;
}

/* table-box2：扩展表格容器 */
.table-box2 {
  width: 1400px;
  border: 1px solid #ebeef5;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  margin-bottom: 40px;
}

/* 以下是重复的样式定义（可能是历史遗留） */
.user-info-box {
  width: 600px;
}

.user-info-tit {
  background: #fafafa;
  color: #000;
  font-weight: 600;
  text-align: center;
  line-height: 34px;
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
}

.info-box {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  line-height: 34px;
  border-bottom: 1px solid #ebeef5;
  border-right: 1px solid #ebeef5;
}

.info-name {
  background: #fafafa;
  border-right: 1px solid #ebeef5;
  width: 30%;
  padding-left: 10px;
  font-weight: 600;
}

.user-info-box .info-box .info {
  padding-left: 10px;
  color: #226003;
  width: 80%;
}
</style>
